import { apiClient, idempotentConfig, unwrap } from './client';
import type {
  ApiEnvelope,
  ServerMatrixView,
  UamEnabledRequest,
  UamInviteRequest,
  UamInviteView,
  UamRolesRequest,
  UamSessionListView,
  UamSessionsRevokedView,
  UamSpokeRequest,
  UamUserListView,
  UamUserView,
} from './types';

/**
 * IAM — UAM (user access management). The Keycloak read-through directory for the caller's hub realm
 * (§UAM of the FROZEN product-api.v1.yaml). List + invite + role/spoke/enabled mutations; every
 * mutation carries an `ol-idempotency-key` (ULID, reusing the api-client helper) and the server
 * enforces the real gates (hub_admin; auditor may read). Errors surface via normalizeApiError at the
 * call site — this module stays a thin, contract-faithful transport, exactly like tenantAdmin.ts.
 *
 * Guards the caller must render (server-authored ApiError.code):
 *   409 USERNAME_TAKEN (invite)              — inline on the username field
 *   422 UAM_ROLE_UNKNOWN / UAM_ROLE_FORBIDDEN — bad/forbidden role in the set (platform_admin ⇒ forbidden)
 *   422 UAM_SPOKE_UNKNOWN                     — spoke not under /spokes
 *   422 UAM_SELF_FORBIDDEN                    — acting on the caller's own account (no self-escalation/lockout)
 */

const enc = (s: string) => encodeURIComponent(s);

/**
 * GET /v1/uam/users — one username-ordered page. `first` is the row offset (default 0), `max` the
 * page size (server default 100, cap 200). The wire view carries no total count, so callers page
 * with prev/next (a full page implies more). Defensive: never assume a well-formed body.
 */
export async function listUsers(first = 0, max = 100): Promise<UamUserListView> {
  const { data } = await apiClient.get<ApiEnvelope<UamUserListView>>('/v1/uam/users', {
    params: { first, max },
  });
  const body = unwrap(data);
  return {
    items: Array.isArray(body?.items) ? body.items : [],
    first: typeof body?.first === 'number' ? body.first : first,
    max: typeof body?.max === 'number' ? body.max : max,
  };
}

/**
 * POST /v1/uam/users — create + role-assign + optional spoke bind. Returns the created user AND the
 * one-time `temporaryPassword` (rides ONLY this 201). 409 USERNAME_TAKEN / 422 UAM_ROLE_* /
 * UAM_SPOKE_UNKNOWN are handled by the caller.
 */
export async function inviteUser(req: UamInviteRequest, idemKey?: string): Promise<UamInviteView> {
  const { data } = await apiClient.post<ApiEnvelope<UamInviteView>>(
    '/v1/uam/users',
    req,
    idempotentConfig(idemKey),
  );
  return unwrap(data);
}

/**
 * PUT /v1/uam/users/{userId}/roles — REPLACE-SET of catalogue roles (the request set becomes the
 * user's full role set). 422 UAM_ROLE_UNKNOWN | UAM_ROLE_FORBIDDEN | UAM_SELF_FORBIDDEN.
 */
export async function replaceUserRoles(
  userId: string,
  req: UamRolesRequest,
  idemKey?: string,
): Promise<UamUserView> {
  const { data } = await apiClient.put<ApiEnvelope<UamUserView>>(
    `/v1/uam/users/${enc(userId)}/roles`,
    req,
    idempotentConfig(idemKey),
  );
  return unwrap(data);
}

/**
 * PUT /v1/uam/users/{userId}/spoke — bind to exactly one spoke, or `spokeId: null` to clear to hub
 * scope (the ADR-007 degenerate). 422 UAM_SPOKE_UNKNOWN | UAM_SELF_FORBIDDEN.
 */
export async function setUserSpoke(
  userId: string,
  req: UamSpokeRequest,
  idemKey?: string,
): Promise<UamUserView> {
  const { data } = await apiClient.put<ApiEnvelope<UamUserView>>(
    `/v1/uam/users/${enc(userId)}/spoke`,
    req,
    idempotentConfig(idemKey),
  );
  return unwrap(data);
}

/**
 * PUT /v1/uam/users/{userId}/enabled — disable/enable. Disabling kills new logins immediately;
 * existing access tokens age out at their (short) lifespan. 422 UAM_SELF_FORBIDDEN (no self-lockout).
 */
export async function setUserEnabled(
  userId: string,
  req: UamEnabledRequest,
  idemKey?: string,
): Promise<UamUserView> {
  const { data } = await apiClient.put<ApiEnvelope<UamUserView>>(
    `/v1/uam/users/${enc(userId)}/enabled`,
    req,
    idempotentConfig(idemKey),
  );
  return unwrap(data);
}

/**
 * GET /v1/uam/users/{userId}/sessions — the user's live SSO sessions (uam.sessions.read:
 * hub_admin, auditor). An empty list is an honest "not signed in anywhere", never an error.
 * 404 UAM_USER_NOT_FOUND when the user is gone.
 */
export async function listUserSessions(userId: string): Promise<UamSessionListView> {
  const { data } = await apiClient.get<ApiEnvelope<UamSessionListView>>(
    `/v1/uam/users/${enc(userId)}/sessions`,
  );
  const body = unwrap(data);
  return {
    userId: body?.userId ?? userId,
    sessions: Array.isArray(body?.sessions) ? body.sessions : [],
  };
}

/**
 * DELETE /v1/uam/users/{userId}/sessions — revoke ALL of the user's sessions (uam.sessions.revoke:
 * hub_admin, a STEP_UP lane). Naturally idempotent (no ol-idempotency-key, the house DELETE
 * convention). Wire honesty the UI must repeat: revocation kills the SSO sessions so no NEW tokens
 * can be minted; already-issued access tokens age out at their short lifespan — and revoking your
 * OWN sessions signs you out everywhere (allowed by design).
 */
export async function revokeUserSessions(userId: string): Promise<UamSessionsRevokedView> {
  const { data } = await apiClient.delete<ApiEnvelope<UamSessionsRevokedView>>(
    `/v1/uam/users/${enc(userId)}/sessions`,
  );
  return unwrap(data);
}

/**
 * GET /v1/uam/matrix — the permission matrix THE RUNNING SERVER boot-loaded (uam.users.read).
 * Callers fall back to the same-commit bundled copy when this 404s (older server) or 403s
 * (caller holds no uam read) — see admin/permissionMatrix.ts.
 */
export async function fetchServerMatrix(): Promise<ServerMatrixView> {
  const { data } = await apiClient.get<ApiEnvelope<ServerMatrixView>>('/v1/uam/matrix');
  return unwrap(data);
}
